/* MorningPrompt — gentle in-app prompt that appears between wakeup_time and
 * leave_by every weekday morning, asking if the parent wants to open Morgon-läge.
 * Avoids native Web Push complexity; we just nudge when the app is open. */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Sun, X, ArrowRight } from 'lucide-react';
import MorningRescueModal from './MorningRescueModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SNOOZE_KEY = 'morning_prompt_snoozed';

function parseTimeToMin(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}
function nowMin() {
  const d = new Date();
  return d.getHours() * 60 + d.getMinutes();
}
function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

export default function MorningPrompt() {
  const [eligibleKids, setEligibleKids] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [rescueKid, setRescueKid] = useState(null);

  useEffect(() => {
    const check = async () => {
      try {
        // Skip if user already snoozed today
        const snoozed = localStorage.getItem(SNOOZE_KEY);
        if (snoozed === todayKey()) {
          setDismissed(true);
          return;
        }
        // Only weekdays (Mon-Fri)
        const day = new Date().getDay();
        if (day === 0 || day === 6) return;
        const r = await axios.get(`${API}/imorgon/morning/strategies`, { withCredentials: true });
        const cur = nowMin();
        const matches = [];
        for (const k of r.data?.kids || []) {
          const p = k.profile || {};
          if (p.enabled === false) continue;
          const w = parseTimeToMin(p.wakeup_time) ?? 6 * 60 + 30;
          const l = parseTimeToMin(p.leave_by) ?? 8 * 60;
          // Active 30 min before wake until 15 min after leave (in case parent is late)
          if (cur >= (w - 30) && cur <= (l + 15)) {
            matches.push({ id: k.id, name: k.name });
          }
        }
        setEligibleKids(matches);
      } catch {/**/}
    };
    check();
    const id = setInterval(check, 10 * 60 * 1000); // re-check every 10 min
    return () => clearInterval(id);
  }, []);

  if (dismissed || rescueKid || eligibleKids.length === 0) {
    if (rescueKid) {
      return (
        <MorningRescueModal
          kidId={rescueKid.id}
          kidName={rescueKid.name}
          onClose={() => setRescueKid(null)}
        />
      );
    }
    return null;
  }

  const snooze = () => {
    localStorage.setItem(SNOOZE_KEY, todayKey());
    setDismissed(true);
  };

  // Show only first match in the banner (parents can click each kid)
  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[95] w-[94vw] max-w-md" data-testid="morning-prompt">
      <div className="bg-gradient-to-r from-amber-100 via-orange-100 to-rose-100 border-2 border-amber-400 rounded-2xl shadow-lg px-3 py-2.5 flex items-start gap-2.5">
        <Sun className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0 animate-pulse" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-extrabold text-amber-900">God morgon ☀️</div>
          <p className="text-[11px] text-amber-800 leading-snug">
            Vill du öppna Morgon-läge för {eligibleKids.map((k) => k.name).join(', ')}?
          </p>
          <div className="flex gap-1.5 mt-1.5 flex-wrap">
            {eligibleKids.map((k) => (
              <button
                key={k.id}
                onClick={() => setRescueKid(k)}
                data-testid={`morning-prompt-${k.id}`}
                className="text-[11px] px-2.5 py-1 rounded-full bg-amber-500 hover:bg-amber-600 text-white font-bold flex items-center gap-1"
              >
                {k.name} <ArrowRight className="w-3 h-3" />
              </button>
            ))}
          </div>
        </div>
        <button onClick={snooze} data-testid="morning-prompt-dismiss" className="text-amber-700 hover:text-amber-900 p-1" title="Inte idag">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
